'use client';

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAssetsClient,
  createTrpcAssetsTransport,
  type AssetsClient,
  type PrepareAssetUploadInput,
  type UploadAssetInput,
  type ListAssetsInput,
  type GetAssetByIdInput,
  type UpdateAssetInput,
  type DeleteAssetInput,
} from '@impact/assets';
import { trpcVanilla } from '@/lib/trpc';

let assetsClientInstance: AssetsClient | null = null;

function getAssetsClient(): AssetsClient {
  if (!assetsClientInstance) {
    assetsClientInstance = createAssetsClient({
      transport: createTrpcAssetsTransport(trpcVanilla),
      validatePolicy: true,
    });
  }

  return assetsClientInstance;
}

const INFINITE_PAGE_SIZE = 24;

export const assetQueryKeys = {
  all: ['assets'] as const,
  lists: () => [...assetQueryKeys.all, 'list'] as const,
  list: (input: ListAssetsInput) => [...assetQueryKeys.lists(), input] as const,
  infinite: (input: ListAssetsInput) => [...assetQueryKeys.all, 'infinite', input] as const,
  details: () => [...assetQueryKeys.all, 'detail'] as const,
  detail: (assetId: string) => [...assetQueryKeys.details(), assetId] as const,
};

export async function prepareAssetUploadAction(input: PrepareAssetUploadInput) {
  return getAssetsClient().prepareAssetUpload(input);
}

export async function uploadAssetAction(input: UploadAssetInput) {
  return getAssetsClient().uploadAsset(input);
}

export async function completeAssetUploadAction(input: { assetId: string }) {
  return getAssetsClient().completeAssetUpload(input);
}

export async function listAssetsAction(input: ListAssetsInput = {}) {
  return getAssetsClient().listAssets(input);
}

export async function getAssetByIdAction(input: GetAssetByIdInput) {
  return getAssetsClient().getAssetById(input);
}

export async function updateAssetAction(input: UpdateAssetInput) {
  return getAssetsClient().updateAsset(input);
}

export async function deleteAssetAction(input: DeleteAssetInput) {
  return getAssetsClient().deleteAsset(input);
}

export function useAssetsList(input: ListAssetsInput = {}) {
  return useQuery({
    queryKey: assetQueryKeys.list(input),
    queryFn: () => listAssetsAction(input),
    placeholderData: (previousData) => previousData,
  });
}

export function useInfiniteAssetsList(input: Omit<ListAssetsInput, 'limit' | 'offset'> = {}) {
  return useInfiniteQuery({
    queryKey: assetQueryKeys.infinite(input),
    queryFn: ({ pageParam = 0 }) =>
      listAssetsAction({ ...input, limit: INFINITE_PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.pagination.offset + lastPage.pagination.limit;
      return nextOffset < lastPage.pagination.total ? nextOffset : undefined;
    },
    placeholderData: (previousData) => previousData,
  });
}

export function useAssetById(assetId: string | null | undefined) {
  return useQuery({
    queryKey: assetId ? assetQueryKeys.detail(assetId) : [...assetQueryKeys.details(), 'empty'],
    queryFn: () => getAssetByIdAction({ assetId: assetId! }),
    enabled: Boolean(assetId),
    retry: false,
  });
}

export function usePrepareAssetUpload() {
  return useMutation({
    mutationFn: prepareAssetUploadAction,
  });
}

export function useUploadAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadAssetAction,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assetQueryKeys.all });
    },
  });
}

export function useCompleteAssetUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: completeAssetUploadAction,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assetQueryKeys.all });
    },
  });
}

export function useUpdateAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAssetAction,
    onSuccess: (asset) => {
      void queryClient.invalidateQueries({ queryKey: assetQueryKeys.all });
      void queryClient.invalidateQueries({ queryKey: assetQueryKeys.detail(asset.id) });
    },
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAssetAction,
    onSuccess: (asset) => {
      void queryClient.invalidateQueries({ queryKey: assetQueryKeys.all });
      void queryClient.invalidateQueries({ queryKey: assetQueryKeys.detail(asset.id) });
    },
  });
}
